// SVG to data URI, for /svg-to-data-uri. Pure string work, in the browser.
//
// The URL-encoded form keeps the SVG readable and escapes only what a URL or CSS string
// can't hold, along the lines of mini-svg-data-uri: whitespace is collapsed, the double quotes
// around attribute values become single quotes, and %20, =, : and / are left unescaped because
// browsers accept them in a data URI. Unlike that library it leaves text alone, so the picture
// can't change: a quote or a run of spaces inside <text> stays what it was.

/** Collapse runs of whitespace to one space and trim, except inside <text> elements. */
export function collapseWhitespace(svg: string): string {
  return svg
    .trim()
    .split(/(<text[\s>][\s\S]*?<\/text>)/)
    .map((part, i) => (i % 2 ? part : part.replace(/\s+/g, ' ')))
    .join('');
}

// A complete start tag. The SVG has passed the XML parser by now, so every tag has this shape.
const START_TAG = /<[A-Za-z][\w:.-]*(?:\s+[\w:.-]+\s*=\s*(?:"[^"]*"|'[^']*'))*\s*\/?>/g;

/** Turn the double quotes around attribute values into single quotes, where the value allows it. */
function singleQuoteAttributes(svg: string): string {
  // Taking each quoted value whole keeps a closing quote from pairing with the next opening one.
  return svg.replace(START_TAG, (tag) =>
    tag.replace(/"[^"]*"|'[^']*'/g, (value) =>
      value[0] === '"' && !value.includes("'") ? `'${value.slice(1, -1)}'` : value
    )
  );
}

export function prepareSvg(svg: string, collapse: boolean): string {
  const text = svg.replace(/^\uFEFF/, '');
  return collapse ? collapseWhitespace(text) : text.trim();
}

function keepSafe(match: string): string {
  switch (match) {
    case '%20':
      return ' ';
    case '%3D':
      return '=';
    case '%3A':
      return ':';
    case '%2F':
      return '/';
    default:
      // Lowercase hex compresses a little better with gzip.
      return match.toLowerCase();
  }
}

/** data:image/svg+xml,... with only the necessary characters percent-encoded. */
export function urlEncodedDataUri(svg: string): string {
  // Single quotes inside url("...") and src="..." need no escaping. Any double quote that is
  // left, in text or around a value that holds a single quote, is encoded as %22.
  return `data:image/svg+xml,${encodeURIComponent(singleQuoteAttributes(svg)).replace(/%[\dA-F]{2}/g, keepSafe)}`;
}

/** data:image/svg+xml;base64,... of the UTF-8 bytes. */
export function base64DataUri(svg: string): string {
  const bytes = new TextEncoder().encode(svg);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + 0x8000)));
  }
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

function px(n: number): string {
  return `${+n.toFixed(2)}px`;
}

export function cssBackgroundSnippet(uri: string): string {
  return `.icon {\n  background-image: url("${uri}");\n  background-repeat: no-repeat;\n  background-size: contain;\n}`;
}

/** The SVG as a mask, so background-color (or currentColor) sets its color. */
export function cssMaskSnippet(uri: string, width: number, height: number): string {
  return (
    `.icon {\n  width: ${px(width)};\n  height: ${px(height)};\n  background-color: currentColor;\n` +
    `  -webkit-mask: url("${uri}") no-repeat center / contain;\n  mask: url("${uri}") no-repeat center / contain;\n}`
  );
}

export function imgSnippet(uri: string, width: number, height: number): string {
  return `<img src="${uri}" width="${Math.round(width)}" height="${Math.round(height)}" alt="">`;
}
